"use client";

import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";

interface MagneticWrapperProps {
  children: React.ReactElement;
  range?: number; // Distance in pixels to trigger pull
  strength?: number; // Intensity of shift (0 to 1)
}

export default function MagneticWrapper({ children, range = 35, strength = 0.35 }: MagneticWrapperProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onMouseMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const distX = e.clientX - centerX;
      const distY = e.clientY - centerY;
      const distance = Math.hypot(distX, distY);

      if (distance < range) {
        // Draw the child towards mouse coordinates
        gsap.to(el.firstElementChild, {
          x: distX * strength,
          y: distY * strength,
          duration: 0.3,
          ease: "power2.out",
        });
      } else {
        // Return child with elastic rebound
        gsap.to(el.firstElementChild, {
          x: 0,
          y: 0,
          duration: 0.6,
          ease: "elastic.out(1.0, 0.4)",
        });
      }
    };

    const onMouseLeave = () => {
      gsap.to(el.firstElementChild, {
        x: 0,
        y: 0,
        duration: 0.6,
        ease: "elastic.out(1.0, 0.4)",
      });
    };

    window.addEventListener("mousemove", onMouseMove);
    el.addEventListener("mouseleave", onMouseLeave);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      el.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [range, strength]);

  return (
    <div ref={containerRef} className="inline-block">
      {children}
    </div>
  );
}
